import { AddressLineMapper } from "./addressLine";
import { CITY_COUNTRY, COUNTRY_NAMES, PALLET_LABELS } from "./constants";
import { ExtractedEmailMapper } from "./personalEmail";
import type { ExtractedCargo, ExtractedData } from "./types";

const addresses = new AddressLineMapper();
const extractedEmails = new ExtractedEmailMapper();

const LOADING_LINE =
  /(?:^|\n)\s*(?:loading|collection|origin|ophalen|laden|belading|beladen|chargement)(?!\s*date)\s*:\s*([^\n]+)/i;
const UNLOADING_LINE =
  /(?:^|\n)\s*(?:unloading|delivery|destination|dest|levering|lossen|lossing|livraison)(?!\s*(?:date|deadline))\s*:\s*([^\n]+)/i;

const PICKUP_DATE_LABEL =
  /(?:pickup\s*date|loading\s*date|pickup)\s*:?\s*/i;
const UNLOAD_DATE_LABEL =
  /(?:unloading\s*date|unload\s*date|delivery\s*date|delivery\s*deadline|preferred\s+delivery(?:\s+deadline)?)\s*:?\s*/i;

class QuoteDateMapper {
  parse(text: string): { pickupDate?: string; unloadDate?: string } {
    const unloadDate = this.afterLabel(text, UNLOAD_DATE_LABEL);
    const labeledPickup = this.afterLabel(text, PICKUP_DATE_LABEL);
    const pickupDate = labeledPickup ?? (unloadDate ? undefined : this.firstLoose(text, unloadDate));
    return { pickupDate, unloadDate };
  }

  private afterLabel(text: string, label: RegExp): string | undefined {
    const found = text.match(label);
    if (!found || found.index === undefined) return undefined;
    return this.firstLoose(text.slice(found.index + found[0].length));
  }

  private firstLoose(text: string, skip?: string): string | undefined {
    const iso = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
    if (iso && iso[1] !== skip && !Number.isNaN(Date.parse(iso[1]))) return iso[1];
    const numeric = text.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
    if (numeric) {
      const isoDate = `${numeric[3]}-${numeric[2].padStart(2, "0")}-${numeric[1].padStart(2, "0")}`;
      if (isoDate !== skip && !Number.isNaN(Date.parse(isoDate))) return isoDate;
    }
    const monthNames: Record<string, string> = {
      jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
      jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
    };
    const named = text.match(
      /(\d{1,2})\s+(jan(?:uary|uari)?|feb(?:ruary|ruari)?|mar(?:ch|t)?|apr(?:il)?|may|mei|jun[ie]?|jul[iy]?|aug(?:ustus|ust)?|sep(?:tember)?|oct(?:ober)?|okt(?:ober)?|nov(?:ember)?|dec(?:ember)?)\s+(\d{4})/i,
    );
    if (!named) return undefined;
    const key = named[2].toLowerCase().substring(0, 3);
    const isoDate = `${named[3]}-${monthNames[key] || "01"}-${named[1].padStart(2, "0")}`;
    if (isoDate === skip) return undefined;
    return isoDate;
  }
}

const quoteDates = new QuoteDateMapper();

// Offline best-effort parser used as a fallback when the AI backend is
// unavailable or no API base is configured. Produces the same ExtractedData
// shape the backend returns so the apply logic is shared.
export function parseText(text: string): ExtractedData {
  const out: ExtractedData = {};
  const t = text.trim();
  if (!t) return out;

  const lower = t.toLowerCase();
  let loadCity: string | null = null;
  let unloadCity: string | null = null;
  let loadCountry: string | null = null;
  let unloadCountry: string | null = null;

  const fromToMatch = lower.match(/from\s+(.+?)\s+to\s+(.+?)(?:\.|,|$|\n)/i);
  if (fromToMatch) {
    const fromPart = fromToMatch[1].trim();
    const toPart = fromToMatch[2].trim();
    for (const city in CITY_COUNTRY) {
      if (fromPart.includes(city) && !loadCity) {
        loadCity = city.charAt(0).toUpperCase() + city.slice(1);
        loadCountry = CITY_COUNTRY[city];
      }
      if (toPart.includes(city) && !unloadCity) {
        unloadCity = city.charAt(0).toUpperCase() + city.slice(1);
        unloadCountry = CITY_COUNTRY[city];
      }
    }
  }

  const loadingMatch = t.match(LOADING_LINE);
  const unloadingMatch = t.match(UNLOADING_LINE);
  if (loadingMatch) {
    const parsed = addresses.parse(loadingMatch[1]);
    if (!loadCity && parsed.city) loadCity = parsed.city;
    if (!loadCountry && parsed.country) loadCountry = parsed.country;
    if (parsed.postal) out.loadPostal = parsed.postal;
    if (parsed.street) out.loadStreet = parsed.street;
    if (parsed.houseNumber) out.loadHouseNumber = parsed.houseNumber;
  }
  if (unloadingMatch) {
    const parsed = addresses.parse(unloadingMatch[1]);
    if (!unloadCity && parsed.city) unloadCity = parsed.city;
    if (!unloadCountry && parsed.country) unloadCountry = parsed.country;
    if (parsed.postal) out.unloadPostal = parsed.postal;
    if (parsed.street) out.unloadStreet = parsed.street;
    if (parsed.houseNumber) out.unloadHouseNumber = parsed.houseNumber;
  }

  if (!loadCity || !unloadCity) {
    const foundCities: { city: string; cc: string; idx: number }[] = [];
    for (const c in CITY_COUNTRY) {
      const idx = lower.indexOf(c);
      if (idx >= 0) {
        foundCities.push({ city: c.charAt(0).toUpperCase() + c.slice(1), cc: CITY_COUNTRY[c], idx });
      }
    }
    foundCities.sort((a, b) => a.idx - b.idx || b.city.length - a.city.length);
    const unique: typeof foundCities = [];
    for (const fc of foundCities) {
      let dup = false;
      for (const u of unique) {
        if (Math.abs(u.idx - fc.idx) < Math.max(u.city.length, fc.city.length)) {
          dup = true;
          break;
        }
      }
      if (!dup) unique.push(fc);
    }
    if (!loadCity && unique.length >= 1) {
      loadCity = unique[0].city;
      loadCountry = unique[0].cc;
    }
    if (!unloadCity && unique.length >= 2) {
      unloadCity = unique[1].city;
      unloadCountry = unique[1].cc;
    }
  }

  if (!loadCountry) {
    for (const name in COUNTRY_NAMES) {
      if (lower.includes(name)) {
        loadCountry = COUNTRY_NAMES[name];
        break;
      }
    }
  }

  // Postal codes
  const postalCandidates: string[] = [];
  const postalLetterRe = /\b(\d{4,5}\s?[A-Z]{2})\b/g;
  let pm: RegExpExecArray | null;
  while ((pm = postalLetterRe.exec(t)) !== null) {
    const letters = pm[1].replace(/[\d\s]/g, "").toUpperCase();
    if (letters === "KG") continue;
    postalCandidates.push(pm[1].trim());
  }
  const numPostalRe = /\b(\d{5})\b/g;
  while ((pm = numPostalRe.exec(t)) !== null) {
    const ctx = t
      .substring(Math.max(0, pm.index - 15), pm.index + pm[0].length + 15)
      .toLowerCase();
    if (/kg|weight|gewicht|date|pickup|delivery|reference|po-/.test(ctx)) continue;
    if (postalCandidates.indexOf(pm[1]) < 0) postalCandidates.push(pm[1]);
  }

  if (loadCity) out.loadCity = loadCity;
  if (loadCountry) out.loadCountry = loadCountry;
  if (!out.loadPostal && postalCandidates[0]) out.loadPostal = postalCandidates[0];
  if (unloadCity) out.unloadCity = unloadCity;
  if (unloadCountry) out.unloadCountry = unloadCountry;
  if (!out.unloadPostal && postalCandidates[1]) out.unloadPostal = postalCandidates[1];

  // Cargo
  const cargoEntries: ExtractedCargo[] = [];
  let workingText = lower;
  const extract = (regex: RegExp, type: string) => {
    workingText = workingText.replace(regex, (match: string, qty: string) => {
      cargoEntries.push({ type, qty: parseInt(qty, 10) });
      return " ".repeat(match.length);
    });
  };
  extract(
    /(\d+)\s*[x\u00d7]?\s*(?:half[\s-]*pallets?|halve[\s-]*pallets?|1\s*\/\s*2[\s-]*pallets?|\u00bd[\s-]*pallets?)\b/gi,
    PALLET_LABELS.half,
  );
  workingText = workingText.replace(
    /\b(?:half[\s-]*pallets?|halve[\s-]*pallets?|1\s*\/\s*2[\s-]*pallets?|\u00bd[\s-]*pallets?)\b/gi,
    (match: string) => {
      cargoEntries.push({ type: PALLET_LABELS.half, qty: 1 });
      return " ".repeat(match.length);
    },
  );
  extract(/(\d+)\s*[x\u00d7]?\s*(?:cp[\s-]?3(?:\s*pallet)?s?)\b/gi, PALLET_LABELS.cp3);
  workingText = workingText.replace(/\b(?:cp[\s-]?3(?:\s*pallet)?s?)\b/gi, (match: string) => {
    cargoEntries.push({ type: PALLET_LABELS.cp3, qty: 1 });
    return " ".repeat(match.length);
  });
  extract(/(\d+)\s*[x\u00d7]?\s*(?:euro(?:\s*pallet)?s?|eur[\s-]*pallet|ep)\b/gi, PALLET_LABELS.euro);
  extract(/(\d+)\s*[x\u00d7]?\s*(?:block(?:\s*pallet)?s?|blok[\s-]*pallet)\b/gi, PALLET_LABELS.block);
  extract(
    /(\d+)\s*[x\u00d7]?\s*(?:pallet|plt|pal)s?\s*(?:120\s*[x\u00d7]\s*80|80\s*[x\u00d7]\s*120)/gi,
    PALLET_LABELS.euro,
  );
  extract(
    /(\d+)\s*[x\u00d7]?\s*(?:pallet|plt|pal)s?\s*(?:120\s*[x\u00d7]\s*100|100\s*[x\u00d7]\s*120)/gi,
    PALLET_LABELS.block,
  );
  extract(
    /(\d+)\s*[x\u00d7]?\s*(?:pallet|plt|pal)s?\s*(?:115\s*[x\u00d7]\s*115)/gi,
    PALLET_LABELS.cp3,
  );
  extract(
    /(\d+)\s*[x\u00d7]?\s*(?:pallet|plt|pal)s?\s*(?:120\s*[x\u00d7]\s*120)/gi,
    PALLET_LABELS.size120x120,
  );
  extract(
    /(\d+)\s*[x\u00d7]?\s*(?:pallet|plt|pal)s?\s*(?:150\s*[x\u00d7]\s*80|80\s*[x\u00d7]\s*150)/gi,
    PALLET_LABELS.size150x80,
  );
  extract(
    /(\d+)\s*[x\u00d7]?\s*(?:pallet|plt|pal)s?\s*(?:80\s*[x\u00d7]\s*60|60\s*[x\u00d7]\s*80)/gi,
    PALLET_LABELS.size80x60,
  );
  extract(/(\d+)\s*[x\u00d7]?\s*(?:pallet|plt|pal|colli|pall)s?\b/gi, PALLET_LABELS.euro);

  const heightMatch =
    lower.match(/(?:height|h|hoogte|h\u00f6he)[:\s]*(\d{2,3})\s*(?:cm)?/i) ||
    lower.match(/(\d{2,3})\s*cm\s*(?:high|height|h\b)/i);
  const parsedHeight = heightMatch ? parseInt(heightMatch[1], 10) : null;
  const ldmMatch = lower.match(/(\d+(?:[.,]\d+)?)\s*(?:ldm|laadmeters?)\b/i);
  const parsedLdm = ldmMatch ? Number(ldmMatch[1].replace(",", ".")) : NaN;

  if (cargoEntries.length > 0) {
    out.cargo = cargoEntries.map((entry, index) => {
      if (entry.type === PALLET_LABELS.half) return entry;
      return {
        ...entry,
        height: parsedHeight ?? undefined,
        ...(index === 0 && Number.isFinite(parsedLdm) && parsedLdm > 0 ? { ldm: parsedLdm } : {}),
      };
    });
  } else if (Number.isFinite(parsedLdm) && parsedLdm > 0) {
    out.cargo = [{ qty: 1, ldm: parsedLdm }];
  }

  // Weight
  const weightMatch =
    lower.match(/(?:total\s*)?(?:weight|gewicht|poids|gewog)[:\s]*(\d[\d.,]*)\s*(?:kg)?/i) ||
    lower.match(/(\d[\d.,]*)\s*kg\b/i);
  if (weightMatch) out.weight = parseInt(weightMatch[1].replace(",", ""), 10);

  // Goods
  const goodsMatch = lower.match(
    /(?:goods|cargo|freight|product|description|goederen|marchandise|ware)[:\s]+([^\n.;]{3,50})/i,
  );
  if (goodsMatch) {
    out.goods = goodsMatch[1]
      .trim()
      .replace(/,\s*$/, "")
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  // Company
  const companyMatch = t.match(
    /(?:company|firma|bedrijf|client|customer|klant|soci\u00e9t\u00e9)[:\s]+([^\n.;]{2,40})/i,
  );
  if (companyMatch) out.company = companyMatch[1].trim().replace(/,\s*$/, "");
  if (!out.company) {
    const submitted = t.match(/submitted by:\s*[^\n]*?[—–-]+\s*([^<\n]+)/i);
    if (submitted) out.company = submitted[1].trim().replace(/,\s*$/, "");
  }

  const email = extractedEmails.firstBusinessEmail(t);
  if (email) out.email = email;

  const dates = quoteDates.parse(t);
  if (dates.pickupDate) out.pickupDate = dates.pickupDate;
  if (dates.unloadDate) {
    out.unloadDate = dates.unloadDate;
    out.timeWindow = { ...out.timeWindow, deliveryDate: dates.unloadDate };
  }

  // Surcharges
  const surcharges: NonNullable<ExtractedData["surcharges"]> = {};

  const tempMatch = lower.match(
    /(?:temp(?:erature|eratuur)?|\u00b0)[:\s]*(-?\d+)\s*(?:\u00b0?\s*c?)?\s*(?:to|[-\u2013\u2014]|tot)\s*(-?\d+)\s*(?:\u00b0?\s*c)?/i,
  );
  if (tempMatch) {
    surcharges.temperature = true;
    surcharges.tempMin = parseInt(tempMatch[1], 10);
    surcharges.tempMax = parseInt(tempMatch[2], 10);
  }

  if (/\bftl\b|full[\s-]*truck[\s-]*load/i.test(lower)) {
    surcharges.ftl = true;
  }

  if (/\badr\b/i.test(lower)) {
    surcharges.adr = true;
    const adrClassMatch = lower.match(/(?:adr|class|klasse)\s*(\d)/i);
    if (adrClassMatch) surcharges.adrClass = parseInt(adrClassMatch[1], 10);
    const unMatch = lower.match(/un\s*(\d{4})/i);
    if (unMatch) surcharges.adrUn = unMatch[1];
  }

  if (/\b(?:tail\s*lift|klep|laadklep|hayon)\b/i.test(lower)) {
    const atLoading = /(?:tail\s*lift|klep|laadklep|hayon)\s*(?:at|bij)?\s*(?:load|lad)/i.test(lower);
    const atUnloading = /(?:tail\s*lift|klep|laadklep|hayon)\s*(?:at|bij)?\s*(?:unload|los|deliv)/i.test(
      lower,
    );
    if (!atLoading && !atUnloading) {
      surcharges.tailLiftLoad = true;
      surcharges.tailLiftUnload = true;
    } else {
      if (atLoading) surcharges.tailLiftLoad = true;
      if (atUnloading) surcharges.tailLiftUnload = true;
    }
  }

  const refMatch = t.match(/(?:ref(?:erence)?|referentie|PO)[:\s#-]*([A-Z0-9][\w\-]{2,20})/i);
  if (refMatch) surcharges.reference = refMatch[1];

  const notesMatch = t.match(/(?:notes?|opmerking(?:en)?|remark|instructions?)[:\s]+([^\n]{3,80})/i);
  if (notesMatch) surcharges.notes = notesMatch[1].trim();

  if (Object.keys(surcharges).length > 0) out.surcharges = surcharges;

  return out;
}
